import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TextField, useMediaQuery, useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import React, { useRef } from "react";
import { v4 as uuid } from "uuid";
import { Chip } from "../Chip/Chip";
import { FormFieldWrapper } from "../FormFieldWrapper/FormFieldWrapper";
import { autoCompleteStyles } from "../FormFieldWrapper/styles/autoCompleteStyles";
import { mergeRefs } from "../Utils/Refs";
/** Picklist component */
export function PickList(props) {
    const { autofocus, disabled, label, multiSelect, required, selectOptions, value, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, limitTags } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isValuePresent = isPickListValuePresent(value);
    const textRef = useRef(null);
    let allRefs = textRef;
    if (props.inputRef) {
        allRefs = mergeRefs(textRef, props.inputRef);
    }
    const styles = React.useMemo(() => autoCompleteStyles({ isMobile, isFreeText: false, multiLine: false, isValuePresent, required, label, multiSelect, disabled }), [
        isMobile,
        isValuePresent,
        required,
        label,
        disabled,
        multiSelect
    ]);
    /** Assigning correct type of onChange value based on whether multiSelect is true. Need to be non destructured props for the type check to work */
    const handleOnChange = (e, reason, values) => {
        if (props.multiSelect) {
            // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            props.onChange && props.onChange(e, values, reason);
        }
        else {
            // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            props.onChange && props.onChange(e, values, reason);
        }
    };
    const id = props.id ?? uuid();
    const labelId = props.label && `${id}-label`;
    const inputId = `${id}-input`;
    //This is to remove any automatic webkit components on the search box (e.g. The x cancel button) because we provide our own.
    const removeSearchtypeWebkit = () => {
        return {
            // clears the ‘X’ from Internet Explorer
            "input[type='search']::-ms-clear": { display: "none", width: 0, height: 0 },
            "input[type='search']::-ms-reveal": { display: "none", width: 0, height: 0 },
            // clears the ‘X’ from Chrome
            "input[type='search']::-webkit-search-decoration": { display: "none" },
            "input[type='search']::-webkit-search-cancel-button": { display: "none" },
            "input[type='search']::-webkit-search-results-button": { display: "none" },
            "input[type='search']::-webkit-search-results-decoration": { display: "none" }
        };
    };
    return (React.createElement(FormFieldWrapper, { formFieldRef: textRef, isMobile: isMobile, isValuePresent: isValuePresent, inputId: inputId, labelId: labelId, ...props, id: id },
        React.createElement(Autocomplete, { sx: [
                {
                    "& .MuiInputBase-root:hover:not(.Mui-disabled):before": {
                        borderBottom: "1px solid transparent",
                        WebkitTransition: "none"
                    },
                    "&.MuiAutocomplete-root .MuiAutocomplete-inputRoot.MuiFilledInput-root": {
                        paddingLeft: multiSelect ? "7px" : "",
                        paddingTop: multiSelect ? "7px" : ""
                    }
                },
                styles.autocomplete,
                styles.container
            ], disabled: disabled, value: value, getOptionLabel: (option) => option.label ?? option.toString(), 
            // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            onChange: (event, value, reason) => handleOnChange(event, reason, value), options: selectOptions, popupIcon: React.createElement(ExpandMoreIcon, null), multiple: multiSelect, limitTags: limitTags ? limitTags : isMobile ? 1 : 3, getLimitTagsText: (num) => React.createElement(Chip, { variant: "primary", text: `... ${num} More`, disabled: disabled }), renderTags: (value, getTagProps) => {
                return value.map((option, index) => React.createElement(Chip, { variant: "primary", text: option.label, ...getTagProps({ index }), key: option.label, disabled: disabled }));
            }, id: inputId, "aria-labelledby": labelId, renderInput: (params) => (React.createElement(TextField, { inputRef: allRefs, sx: removeSearchtypeWebkit, ...(isMobile && { label: label }), ...params, variant: "filled", autoFocus: autofocus, placeholder: !multiSelect || (multiSelect && !value) || (props.multiSelect && props.value && props.value?.length < 1) ? props.placeholder : undefined, inputProps: {
                    ...params.inputProps,
                    "aria-label": ariaLabel,
                    "aria-labelledby": ariaLabelledBy,
                    autoComplete: "off",
                    type: "search" // Will stop chrome autocomplete suggestions from showing up (Both required)
                } })) })));
}
export const isPickListValuePresent = (value) => {
    if (Array.isArray(value)) {
        // Multiselect: at this stage we are not checking that each item in array has a value
        return value.length > 0;
    }
    else if (value == null || value == undefined || !value) {
        return false;
    }
    else {
        const singleSelectValue = !!(value && value.value);
        return singleSelectValue;
    }
};
// Default props
PickList.defaultProps = {
    multiSelect: false,
    disabled: false,
    required: false,
    labelProps: {
        width: "180px",
        position: "left"
    }
};
